import React from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { growlActionSuccess, growlErrors } from '../../utils/NotificationUtil';
import { withGrowl } from '../../components/GrowlNotifications';
import { growlShape } from '../../components/prop-types';
import ToggleButton from './ToggleButton';
import {
  QUESTIONNAIRE_TRANSLATED,
  JPS_TRANSLATED,
  REPORT_TEXTS_TRANSLATED,
  VALIDATED,
  SECTION,
  SURVEY,
  INTERESTS_SECTION,
} from './constants';

const messages = defineMessages({
  createConversionNorm: {
    id: 'itembank-management.conversion-norm.create',
    defaultMessage: 'Create',
  },
  updateConversionNorm: {
    id: 'itembank-management.conversion-norm.update',
    defaultMessage: 'Update',
  },
});

const TOGGLE_CONTENT_AVAILABILITY_MUTATION = gql`
  mutation toggleContentAvailability($input: ToggleContentAvailabilityInput!) {
    toggleContentAvailability(input: $input) {
      parentContentCode
      parentContentType
      contentCode
      accountId
      enabled
      category
      language
    }
  }
`;

const Content = ({
  accountId,
  selectedLanguage,
  selectedContentCategory: {
    code: parentContentCode,
    displayName: parentContentDisplayName,
    type: parentContentType,
    availableCategories,
  },
  content: {
    code,
    displayName,
    questionnaireLanguages,
    reportLanguages,
    jpsLanguages,
    validatedLanguages,
    conversionNorms,
  },
  growl,
  showDialog,
}) => {
  const { formatMessage } = useIntl();

  const getConversionNorm = () => {
    if (conversionNorms) {
      return conversionNorms.find(norm => norm.language === selectedLanguage)
        ? conversionNorms.find(norm => norm.language === selectedLanguage).conversionNorm
        : null;
    }

    return null;
  };

  const questionnaireTranslated = questionnaireLanguages && questionnaireLanguages.includes(selectedLanguage);
  const reportTextsTranslated = reportLanguages && reportLanguages.includes(selectedLanguage);
  const jpsTranslated = jpsLanguages && jpsLanguages.includes(selectedLanguage);
  const isValidated = validatedLanguages && validatedLanguages.includes(selectedLanguage);
  const conversionNorm = getConversionNorm();

  const conversionNormText = conversionNorm
    ? formatMessage(messages.updateConversionNorm) : formatMessage(messages.createConversionNorm);

  const [toggleContentAvailabilityMutation] = useMutation(TOGGLE_CONTENT_AVAILABILITY_MUTATION);

  const toggleState = async (categoryToToggle, newState) => {
    try {
      await toggleContentAvailabilityMutation({
        variables: {
          input: {
            accountId,
            parentContentCode,
            parentContentType,
            contentCode: code,
            enabled: newState,
            category: categoryToToggle,
            language: selectedLanguage,
          },
        },
        refetchQueries: ['contentCategoryAndActiveLocales'],
      });
      growlActionSuccess(growl);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  const showUpdateConversionNormDialog = () => showDialog(parentContentCode, code, conversionNorm, selectedLanguage);

  return (
    <div className="component">
      {parentContentType === SECTION && <h4 className="dimension-display-name">{displayName}</h4>}
      {parentContentType === SURVEY && (
        <div className="survey-display-names">
          <h4 className="competency-display-name">{parentContentDisplayName}</h4>
          <h4 className="ability-display-name">{displayName}</h4>
        </div>
      )}
      <div className="buttons">
        {availableCategories.includes(QUESTIONNAIRE_TRANSLATED) && (
          <ToggleButton
            enabled={questionnaireTranslated}
            handleClick={() => toggleState(QUESTIONNAIRE_TRANSLATED, !questionnaireTranslated)}
          />
        )}
        {availableCategories.includes(REPORT_TEXTS_TRANSLATED) && (
          <ToggleButton
            enabled={reportTextsTranslated}
            handleClick={() => toggleState(REPORT_TEXTS_TRANSLATED, !reportTextsTranslated)}
          />
        )}
        {availableCategories.includes(JPS_TRANSLATED) && (
          <ToggleButton enabled={jpsTranslated} handleClick={() => toggleState(JPS_TRANSLATED, !jpsTranslated)} />
        )}
        {availableCategories.includes(VALIDATED) && (
          <ToggleButton enabled={isValidated} handleClick={() => toggleState(VALIDATED, !isValidated)} />
        )}
        {parentContentType === SECTION && parentContentCode !== INTERESTS_SECTION && (
          <Button
            label={conversionNormText}
            className="p-button-secondary"
            onClick={showUpdateConversionNormDialog}
          />
        )}
      </div>
    </div>
  );
};

Content.propTypes = {
  accountId: PropTypes.string.isRequired,
  selectedContentCategory: PropTypes.objectOf(PropTypes.any).isRequired,
  selectedLanguage: PropTypes.string.isRequired,
  content: PropTypes.objectOf(PropTypes.any).isRequired,
  growl: growlShape.isRequired,
  showDialog: PropTypes.func.isRequired,
};

export default withGrowl(Content);
